///
/// EarthBrowser
///
/// Created by Matt Giger on 12/2/2025
/// Copyright (c) 2025 Geodata Labs
///

import { vec3 } from "@stovokor/math/vector"
import type { GestureNode } from "./GestureNode"

/**
 * Apple-style gesture recognition system
 *
 * This system layers on top of raw pointer/touch events to provide high-level
 * gesture recognition with proper state management and conflict resolution.
 */

/** key states during gesture */
export interface KeyState {
	ctrlKey: boolean
	shiftKey: boolean
	altKey: boolean
	metaKey: boolean
}

export interface GestureHitResult {
	node: GestureNode
	hitPoint: vec3
	distance: number
}

// Touch point tracking for gesture recognition
export class TouchPoint {
	readonly id: number
	public x: number
	public y: number
	readonly startX: number
	readonly startY: number
	readonly timestamp: number
	public keys: KeyState
	/** PointerEvent.pointerType: "mouse" | "pen" | "touch" (handlers tune feel per input) */
	readonly pointerType: string

	// The gesture hits and recognizers that were used to recognize this touch
	// These values are passed on to other events that match this touch (captured pointer events)
	public gestureHits: GestureHitResult[] = []
	public gestureRecognizers: AnyGestureRecognizer[] = []

	constructor(id: number, x: number, y: number, startX: number, startY: number, timestamp: number, keys: KeyState, pointerType = "mouse") {
		this.id = id
		this.x = x
		this.y = y
		this.startX = startX
		this.startY = startY
		this.timestamp = timestamp
		this.keys = keys
		this.pointerType = pointerType
	}
}

// Gesture state machine (matches Apple's UIGestureRecognizer states)
export enum GestureState {
	// Initial state - gesture has not yet been recognized
	Possible = "possible",
	// For continuous gestures: gesture has started being recognized
	Began = "began",
	// For continuous gestures: gesture data has changed
	Changed = "changed",
	// Gesture has completed successfully
	Ended = "ended",
	// Gesture was cancelled (e.g., system interruption)
	Cancelled = "cancelled",
	// Gesture recognition failed (e.g., didn't match pattern)
	Failed = "failed"
}

export type GestureType =
	"pan" |
	"tap" |
	"doubleTap" |
	"longPress" |
	"zoom" |
	"rotate" |
	"pinch" |
	"hover" |
	"swipe"


// Hit info for a single touch point on the owner node
export interface OwnerHit {
	hitPoint: vec3
	hitDistance: number
}

// Base gesture event data
export interface GestureEventData {
	type: GestureType
	state: GestureState
	timestamp: number
	// Location in canvas coordinates
	x: number
	y: number
	// Hit info from all touch points that hit the recognizer's owner node
	ownerHits: OwnerHit[]
	// Modifier key state at gesture time
	keys?: KeyState
}

// Callback type for gesture state changes
export type GestureCallback<T extends GestureEventData> = (gesture: T) => void

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyGestureRecognizer = GestureRecognizer<any>

// Configuration for gesture recognition behavior
export interface GestureRecognizerConfig {
	// Allow this gesture to be recognized simultaneously with others
	allowSimultaneous?: boolean
	// Gestures that must fail before this one can be recognized
	requireToFail?: AnyGestureRecognizer[]
	// Minimum number of touches required
	minimumTouches?: number
	// Maximum number of touches allowed
	maximumTouches?: number
	// Whether gesture is enabled
	enabled?: boolean
}

/**
 * Abstract base class for gesture recognizers
 *
 * Implements the state machine pattern from Apple's UIGestureRecognizer.
 * Subclasses implement specific gesture patterns (tap, pan, pinch, etc.)
 */
export abstract class GestureRecognizer<T extends GestureEventData = GestureEventData> {
	protected _state: GestureState = GestureState.Possible
	protected _enabled: boolean = true
	protected _allowSimultaneous: boolean = false
	protected _requireToFail: AnyGestureRecognizer[] = []
	protected _minimumTouches: number = 1
	protected _maximumTouches: number = Number.MAX_SAFE_INTEGER

	// Active touch points being tracked
	protected touches = new Map<number, TouchPoint>()

	// Callbacks
	protected callbacks = new Set<GestureCallback<T>>()

	// The GestureNode that owns this recognizer (set automatically when adding to a node)
	public owner?: GestureNode

	// Priority for arena conflict resolution (higher = more priority)
	public priority: number = 0

	constructor(config: GestureRecognizerConfig = {}) {
		this._allowSimultaneous = config.allowSimultaneous ?? false
		this._requireToFail = config.requireToFail ?? []
		this._minimumTouches = config.minimumTouches ?? 1
		this._maximumTouches = config.maximumTouches ?? Number.MAX_SAFE_INTEGER
		this._enabled = config.enabled ?? true
	}

	// Public accessors
	get state(): GestureState { return this._state }
	get enabled(): boolean { return this._enabled }
	set enabled(value: boolean) { this._enabled = value }
	get allowSimultaneous(): boolean { return this._allowSimultaneous }
	set allowSimultaneous(value: boolean) { this._allowSimultaneous = value }
	get requireToFail(): AnyGestureRecognizer[] { return this._requireToFail }
	set requireToFail(value: AnyGestureRecognizer[]) { this._requireToFail = value }
	get minimumTouches(): number { return this._minimumTouches }
	get maximumTouches(): number { return this._maximumTouches }
	set maximumTouches(value: number) { this._maximumTouches = value }
	get touchCount(): number { return this.touches.size }

	// Check if required gestures have failed
	protected canRecognize(): boolean {
		return this._enabled && this._requireToFail.every(g => g.state === GestureState.Failed)
	}

	// State transition with validation
	protected setState(newState: GestureState): void {
		const validTransitions = this.getValidTransitions()
		if (!validTransitions.includes(newState)) {
			console.warn(`Invalid gesture state transition: ${this._state} -> ${newState}`)
			return
		}
		this._state = newState
	}

	// Get valid state transitions from current state
	private getValidTransitions(): GestureState[] {
		switch (this._state) {
			case GestureState.Possible:
				// Ended is valid for discrete gestures (tap) that skip Began/Changed
				return [GestureState.Began, GestureState.Ended, GestureState.Failed, GestureState.Cancelled]
			case GestureState.Began:
				return [GestureState.Changed, GestureState.Ended, GestureState.Cancelled]
			case GestureState.Changed:
				return [GestureState.Changed, GestureState.Ended, GestureState.Cancelled]
			case GestureState.Ended:
			case GestureState.Cancelled:
			case GestureState.Failed:
				return [GestureState.Possible] // Can reset
			default:
				return []
		}
	}

	// Subscribe to gesture events
	public onGesture(callback: GestureCallback<T>): () => void {
		this.callbacks.add(callback)
		return () => this.callbacks.delete(callback)
	}

	// Emit gesture event to all subscribers
	protected emit(data: T): void {
		this.callbacks.forEach(cb => cb(data))
	}

	// Reset to initial state
	public reset(): void {
		this._state = GestureState.Possible
		this.touches.clear()
	}

	// Cancel the gesture
	public cancel(): void {
		if (this._state === GestureState.Began || this._state === GestureState.Changed) {
			this.setState(GestureState.Cancelled)
			this.emit(this.createEventData(GestureState.Cancelled))
		}
		this.reset()
	}

	/**
	 * Called by the arena when this recognizer has won the competition
	 * and become the active gesture handler. Override to perform
	 * actions that should only happen after winning (like requesting focus).
	 */
	public didBecomeActive(): void {
		// Override in subclasses
	}

	// Check if this gesture is currently active (began or changed)
	public isActive(): boolean {
		return this._state === GestureState.Began || this._state === GestureState.Changed
	}

	// Check if this gesture is in a terminal state
	public isTerminal(): boolean {
		return this._state === GestureState.Ended ||
			   this._state === GestureState.Cancelled ||
			   this._state === GestureState.Failed
	}

	// Get centroid of all tracked touches
	protected getCentroid(): { x: number, y: number } {
		if (this.touches.size === 0) {
			return { x: 0, y: 0 }
		}

		let sumX = 0, sumY = 0
		this.touches.forEach(touch => {
			sumX += touch.x
			sumY += touch.y
		})

		return {
			x: sumX / this.touches.size,
			y: sumY / this.touches.size
		}
	}

	// Get all tracked touches as an array
	protected getTouchArray(): TouchPoint[] {
		return Array.from(this.touches.values())
	}

	// Find all hit results for this recognizer's owner node from all tracked touches
	protected getOwnerHits(): OwnerHit[] {
		if (!this.owner) return []

		const hits: OwnerHit[] = []
		for (const touch of this.touches.values()) {
			const hit = touch.gestureHits.find(h => h.node === this.owner)
			if (hit) {
				hits.push({
					hitPoint: hit.hitPoint,
					hitDistance: hit.distance
				})
			}
		}
		return hits
	}

	// Abstract methods to be implemented by subclasses
	abstract handleTouchStart(touches: TouchPoint[]): void
	abstract handleTouchMove(touches: TouchPoint[]): void
	abstract handleTouchEnd(touches: TouchPoint[]): void
	abstract handleTouchCancel(): void
	protected abstract createEventData(state: GestureState): T

	// Identifier for this gesture type
	abstract get gestureType(): string
}

// Discrete gesture recognizer - for gestures that happen at a single point in time (tap, double-tap)
export abstract class DiscreteGestureRecognizer<T extends GestureEventData = GestureEventData> extends GestureRecognizer<T> {
	// Pending event data - stored when recognized, emitted when arena confirms winner
	private pendingEventData?: T
	// Track if recognition was deferred (async) - in which case we emit directly
	private isDeferred = false
	// Track if another recognizer won and this one was suppressed
	private wasSuppressed = false

	/**
	 * Mark this recognition as deferred (will be called asynchronously)
	 * Call this before scheduling a setTimeout that will call recognize()
	 */
	protected markDeferred(): void {
		this.isDeferred = true
	}

	/**
	 * Called by arena to suppress this recognizer when another discrete gesture won.
	 * Prevents deferred recognition from emitting later.
	 */
	public suppress(): void {
		this.wasSuppressed = true
		this.isDeferred = false
		this.pendingEventData = undefined
	}

	/**
	 * Clear suppression state (called when starting a new gesture session)
	 */
	public clearSuppression(): void {
		this.wasSuppressed = false
	}

	// Discrete gestures go directly from Possible to Ended (or Failed)
	protected recognize(): void {
		// Don't recognize if suppressed by arena
		if (this.wasSuppressed) {
			return
		}

		if (this.canRecognize()) {
			this.setState(GestureState.Ended)
			const eventData = this.createEventData(GestureState.Ended)

			if (this.isDeferred) {
				// Deferred recognition - emit directly since arena context is gone
				this.emit(eventData)
				this.isDeferred = false
				setTimeout(() => this.reset(), 0)
			} else {
				// Immediate recognition - store data and let arena resolve conflicts
				this.pendingEventData = eventData
			}
		}
	}

	/**
	 * Called by arena when this recognizer wins conflict resolution.
	 * For discrete gestures, this is when we actually emit the event.
	 */
	public override didBecomeActive(): void {
		if (this.pendingEventData) {
			this.emit(this.pendingEventData)
			this.pendingEventData = undefined
		}
		// Auto-reset after emitting for discrete gestures
		setTimeout(() => this.reset(), 0)
	}

	protected fail(): void {
		this.setState(GestureState.Failed)
		this.pendingEventData = undefined
		this.isDeferred = false
		// Don't clear wasSuppressed here - keep it until reset
		// Don't auto-reset - stay in Failed state so requireToFail dependents can check
		// The arena will reset recognizers when the gesture session ends
	}

	public override reset(): void {
		super.reset()
		this.pendingEventData = undefined
		this.isDeferred = false
		this.wasSuppressed = false
	}
}

// Continuous gesture recognizer - for gestures that track over time (pan, pinch, rotation)
export abstract class ContinuousGestureRecognizer<T extends GestureEventData = GestureEventData> extends GestureRecognizer<T> {
	// Whether this recognizer has won the arena and can emit events
	private hasWonArena = false
	// Pending began event data (stored until arena confirms winner)
	private pendingBeganData?: T

	/**
	 * Called by arena when this recognizer wins conflict resolution.
	 * For continuous gestures, this is when we emit the pending Began event.
	 */
	public override didBecomeActive(): void {
		this.hasWonArena = true
		if (this.pendingBeganData) {
			this.emit(this.pendingBeganData)
			this.pendingBeganData = undefined
		}
	}

	// Start continuous gesture recognition
	protected begin(data: T): void {
		if (this.canRecognize()) {
			this.setState(GestureState.Began)
			// Store began data - will be emitted when arena confirms winner
			this.pendingBeganData = { ...data, state: GestureState.Began }
		}
	}

	// Update continuous gesture
	protected change(data: T): void {
		if (this._state === GestureState.Began || this._state === GestureState.Changed) {
			this.setState(GestureState.Changed)
			// Only emit if we've won the arena
			if (this.hasWonArena) {
				this.emit({ ...data, state: GestureState.Changed })
			}
		}
	}

	// End continuous gesture
	protected end(data: T): void {
		if (this._state === GestureState.Began || this._state === GestureState.Changed) {
			this.setState(GestureState.Ended)
			// Only emit if we've won the arena
			if (this.hasWonArena) {
				this.emit({ ...data, state: GestureState.Ended })
			}
		}
		this.reset()
	}

	protected fail(): void {
		this.setState(GestureState.Failed)
		this.pendingBeganData = undefined
		this.reset()
	}

	public override reset(): void {
		super.reset()
		this.hasWonArena = false
		this.pendingBeganData = undefined
	}
}

